import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';

import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';

import { finShare } from '@fcom/rx';
import { NotificationTheme } from '@fcom/ui-components';
import { isFlight } from '@fcom/common-booking/utils/common-booking.utils';

import {
  Category,
  FinnairCart,
  FinnairItineraryItemFlight,
  FinnairOrder,
  FinnairServiceItem,
  FinnairServiceSegmentItem,
} from '../../api/models';
import { FlightServices } from '../../interfaces';
import { getGroupedServices } from '../../utils/service-utils';

@Component({
  selector: 'fin-dapi-included-service',
  templateUrl: './included-service.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DapiIncludedServiceComponent implements OnInit {
  readonly Category = Category;
  readonly NotificationTheme = NotificationTheme;

  @Input() service: FinnairServiceItem;
  @Input() showAsPending = false;
  @Input() cartOrOrder$: Observable<FinnairCart | FinnairOrder>;

  serviceSegmentItems$: Observable<FlightServices[]>;

  ngOnInit(): void {
    this.serviceSegmentItems$ = this.parseBoundData();
  }

  /**
   * Merge Service Segments
   */
  private mergeServiceSegments(flight: FinnairItineraryItemFlight): FlightServices {
    // If multiple passengers has extra bags for same segment, backend
    // returns own segment for each passenger. Let's merge them here
    // possibly would be good to change the format on backend
    const serviceSegments: FinnairServiceSegmentItem[] =
      this.service.bounds
        .reduce((segments, serviceBound) => segments.concat(serviceBound.segments), [])
        .filter((segment) => segment.id === flight.id) || [];

    return serviceSegments.reduce(
      (mergedSegment, segment) => ({
        ...segment,
        operatingAirline: flight.operatingAirline,
        passengers: (mergedSegment.passengers || []).concat(segment.passengers).map((i) => {
          return {
            ...i,
            groupedServices: getGroupedServices(i.services, this.showAsPending),
          };
        }),
      }),
      {} as FlightServices
    );
  }

  /**
   * Parses and flattens the bounds in the booking and adds some additional information
   * to make the templating a bit more simpler. Some changes expected from the backend response
   * to reduce the amount of logic here.
   */
  parseBoundData(): Observable<FlightServices[]> {
    return this.cartOrOrder$.pipe(
      map((booking) => {
        const segments = booking.bounds
          .map((bound) => {
            return bound.itinerary
              .filter(isFlight)
              .map((flight) => this.mergeServiceSegments(flight))
              .filter((s) => s.passengers?.length > 0);
          })
          .reduce((a, b) => a.concat(b), []);
        // For Cancellation Cover display info only once whole whole trip, not for each bound
        return this.service.category === Category.COVER ? segments.slice(0, 1) : segments;
      }),
      finShare()
    );
  }
}
